import { useQuery, useReactiveVar } from '@apollo/client/react';
import { authVar } from '@/lib/apollo/auth';
import {
  AiCreditsDocument,
  type AiCreditsQuery,
} from '~/graphql/generated/graphql';

/** The `currentUser.aiCredits` shape, taken straight from the codegen'd query. */
export type AiCredits = NonNullable<AiCreditsQuery['currentUser']>['aiCredits'];

/**
 * The current user's AI (FYT) credit standing. `credits` is null until loaded (or when
 * unauthenticated). Sending an AI message should be disabled when `balance <= 0`.
 *
 * Backed by the codegen'd `AiCredits` operation
 * (`features/purchases/graphql/documents/AiCredits.query.gql`) — never an inline `gql`
 * string.
 */
export const useAiCredits = (): {
  credits: AiCredits | null;
  loading: boolean;
  /** Re-query the balance — e.g. after buying a credit pack. */
  refetch: () => Promise<unknown>;
} => {
  const authTokens = useReactiveVar(authVar);
  const { data, loading, refetch } = useQuery(AiCreditsDocument, {
    skip: !authTokens,
    // Don't let a transient error throw in the UI.
    errorPolicy: 'all',
  });
  return { credits: data?.currentUser?.aiCredits ?? null, loading, refetch };
};
